<script lang="ts">
  import AuthLayout from "./AuthLayout.svelte";
  import Banner from "$lib/components/Banner.svelte";
  import Button from "$lib/components/Button.svelte";
  import { formatVerificationCode, isCompleteVerificationCode } from "$lib/auth/verificationCode";

  // Keep the one-time code out of request logs and browser history, just like the
  // automatic verification token. It remains in memory only long enough to display.
  const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const code = formatVerificationCode(fragment.get("code") ?? "");
  const requestId = fragment.get("request") ?? "";
  if (window.location.hash) {
    window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
  }

  let copied = $state(false);
  let copyFailed = $state(false);
  const complete = isCompleteVerificationCode(code) &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(requestId);

  async function copyCode(): Promise<void> {
    copyFailed = false;
    try {
      await navigator.clipboard.writeText(code);
      copied = true;
    } catch {
      const input = document.querySelector<HTMLInputElement>("#email-login-code");
      input?.focus();
      input?.select();
      copyFailed = true;
    }
  }
</script>

<AuthLayout title="Login via code" subtitle="Use this code in your original EpiDev sign-in tab.">
  {#if !complete}
    <Banner tone="error" title="Code link incomplete">
      This link is missing a valid one-time code. Request a fresh sign-in email and use
      the newest message.
    </Banner>
    <Button href="/login" fullWidth>Back to sign in</Button>
  {:else}
    <div class="stack">
      <label for="email-login-code">One-time login code</label>
      <input
        id="email-login-code"
        class="code"
        value={code}
        readonly
        spellcheck="false"
        aria-describedby="code-guidance"
      />
      <Button fullWidth onclick={copyCode}>{copied ? "Copied" : "Copy code"}</Button>
      <p id="code-guidance" class="guidance">
        Return to the tab where you entered your email, paste this code, and continue.
        The code expires shortly and can be used once.
      </p>
      {#if copyFailed}
        <p class="copy-failed" role="status">The code is selected. Copy it with your browser or keyboard.</p>
      {/if}
    </div>
  {/if}
</AuthLayout>

<style>
  label {
    color: var(--muted);
    font-size: 13px;
    font-weight: 600;
  }
  .code {
    width: 100%;
    padding: 14px 12px;
    border: 1px solid var(--line-soft);
    border-radius: var(--radius-btn);
    background: var(--bg-2);
    color: var(--text);
    font-family: var(--font-mono);
    font-size: 20px;
    letter-spacing: 0;
    text-align: center;
  }
  .guidance,
  .copy-failed {
    margin: 0;
    color: var(--muted);
    font-size: 13px;
  }
</style>
